import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import WelcomeBot from '../components/WelcomeBot';
import BadgeVerificado from '../components/BadgeVerificado';
import PerfilCompleto, { hijosLabel } from '../components/PerfilCompleto';
import '../styles/Extras.css';
import '../styles/ExplorePage.css';
import { DELIVERY, DELIVERY_SRCSET } from '../lib/imagenes';
import { PROVINCIAS, DEPARTAMENTOS_POR_PROVINCIA, lugarDe } from '../lib/ubicacion';

const EDUCACION_OPS = ['','Primaria','Secundaria','Terciaria/Técnica','Universitaria en curso','Universitaria completa','Posgrado'];
const ESTILO_VIDA_OPS = ['','Tranquilo/Hogareño','Activo/Deportista','Social/Fiestero','Aventurero','Trabajólico','Equilibrado'];
const HIJOS_OPS = ['','No tengo','1','2','3','4','5','6','7','8','9','10','Más de 10'];
const ESTADO_CIVIL_OPS = ['','Soltero/a','En una relación','Comprometido/a','En unión convivencial','Casado/a','Separado/a','Divorciado/a','Viudo/a','Relación abierta','Es complicado'];
const QUIERE_HIJOS_OPS = ['','Sí, quiero','No quiero','No sé todavía','Depende de la pareja'];
const RELIGION_OPS = ['','Sin religión','Católico/a','Cristiano/a','Judío/a','Musulmán/a','Agnóstico/a','Otro'];
const GENERO_OPS = ['','Mujer','Hombre','Mujer trans','Hombre trans','No binario','Género fluido','Agénero','Travesti','Otra identidad'];
const ORIENTACION_OPS = ['','Heterosexual','Gay','Lesbiana','Bisexual','Pansexual','Asexual','Demisexual','Queer'];
const BUSCA_OPS = ['','Amistad','Compañía','Algo serio','Una relación','Algo casual'];
const CONTEXTURA_OPS = ['','Delgada','Atlética','Promedio','Robusta','Con curvas','Algunos kilos de más'];
const PERSONALIDAD_OPS = ['','Extrovertido/a','Introvertido/a','Divertido/a','Romántico/a','Aventurero/a','Tranquilo/a',
  'Hogareño/a','Fiestero/a','Deportista','Intelectual','Creativo/a','Espiritual',
  'Sincero/a','Cariñoso/a','Responsable','Optimista','Independiente','Familiero/a'];

// Aviso de Cupido Delivery para el plan gratis: sale cada 2 inicios de sesión y se puede cerrar.
// El cierre se recuerda por sesión (sessionStorage), así no vuelve a aparecer hasta el próximo aviso.
function DeliveryAviso({ user, onVerPlanes }) {
  const n = user?.login_count || 0;
  const key = `cd_aviso_${n}`;
  const [cerrado, setCerrado] = useState(() => {
    try { return Boolean(sessionStorage.getItem(key)); } catch { return false; }
  });
  if (user?.plan !== 'free' || n === 0 || n % 2 !== 0 || cerrado) return null;
  const cerrar = () => { try { sessionStorage.setItem(key, '1'); } catch {} setCerrado(true); };
  return (
    <div className="delivery-aviso" role="note">
      <img src={DELIVERY} srcSet={DELIVERY_SRCSET} sizes="(max-width: 600px) 48px, 60px" alt="Cupido Delivery" className="delivery-aviso-logo" />
      <div className="delivery-aviso-text">
        <strong>Cupido Delivery</strong> · Sorprendé a tu conexión con flores, bombones o un desayuno de comercios de San Juan. Incluido en Mate y Premium.
      </div>
      <button className="btn btn-sm btn-outline" onClick={onVerPlanes}>Ver planes</button>
      <button className="delivery-aviso-close" onClick={cerrar} aria-label="Cerrar aviso">✕</button>
    </div>
  );
}

// Nota de 24 h, estilo Instagram: se escribe acá y se ve en tu perfil y en el chat
function NotaEditor({ user, onGuardada }) {
  const [texto, setTexto] = useState('');
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const { success, error } = useToast();
  const nota = user?.nota;

  const guardar = async () => {
    if (!texto.trim()) return;
    setGuardando(true);
    try { await api.guardarNota(texto.trim()); success('📝 ¡Nota publicada por 24 horas!'); setEditando(false); setTexto(''); onGuardada(); }
    catch (e) { error(e.message || 'No se pudo guardar la nota.'); }
    finally { setGuardando(false); }
  };
  const borrar = async () => {
    try { await api.borrarNota(); onGuardada(); } catch (e) { error(e.message || 'No se pudo borrar la nota.'); }
  };

  return (
    <div className="nota-editor">
      <h3 className="filter-title">📝 Tu nota</h3>
      {nota && !editando ? (
        <>
          <div className="nota-burbuja nota-burbuja--propia">{nota}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-sm btn-outline" onClick={() => { setTexto(nota); setEditando(true); }}>Cambiar</button>
            <button className="btn btn-sm btn-ghost" onClick={borrar}>Borrar</button>
          </div>
        </>
      ) : (
        <>
          <p className="nota-ayuda">Contá en una frase qué tenés ganas de hacer. Se ve 24 horas en tu perfil.</p>
          <input className="form-input" maxLength={60} value={texto} placeholder="¿Alguien para un mate en el Parque de Mayo?"
            onChange={e => setTexto(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') guardar(); }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <button className="btn btn-sm btn-primary" onClick={guardar} disabled={guardando || !texto.trim()}>{guardando ? 'Publicando…' : 'Publicar nota'}</button>
            {editando && <button className="btn btn-sm btn-ghost" onClick={() => setEditando(false)}>Cancelar</button>}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--texto-muted)' }}>{texto.length}/60</span>
          </div>
        </>
      )}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = (Date.now() - new Date(dateStr + 'Z').getTime()) / 1000;
  if (diff < 120) return '🟢 Conectado/a ahora';
  if (diff < 3600) return `🕐 Hace ${Math.floor(diff/60)} min`;
  if (diff < 86400) return `🕐 Hace ${Math.floor(diff/3600)}h`;
  return `🕐 Hace ${Math.floor(diff/86400)} días`;
}

function MatchModal({ matchedUser, onClose, onChat }) {
  return (
    <div className="match-overlay" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ textAlign: 'center', padding: '24px 32px' }}>
        <div style={{ fontSize: 80, marginBottom: 12, animation: 'heartBeat 1.5s ease infinite' }}>💕</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 48, color: 'white', marginBottom: 8 }}>¡Hay conexión!</div>
        <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 18, marginBottom: 32 }}>
          Vos y <strong>{matchedUser.name}</strong> se gustaron 🧉
        </div>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-lg" onClick={onClose}>Seguir explorando</button>
          <button className="btn btn-dorado btn-lg" onClick={onChat}>Ir al chat 💬</button>
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ profile, onLike, onSuperLike, onSkip, onWink, onVerPerfil, isTop, plan }) {
  const cardRef = useRef(null);
  const startPos = useRef(null);
  const isDragging = useRef(false);
  const currentDx = useRef(0);
  const [likeOpacity, setLikeOpacity] = useState(0);
  const [nopeOpacity, setNopeOpacity] = useState(0);
  const [exiting, setExiting] = useState(null);

  const triggerExit = useCallback((dir) => {
    if (!cardRef.current || exiting) return;
    setExiting(dir);
    setTimeout(() => { dir === 'right' ? onLike() : onSkip(); }, 350);
  }, [exiting, onLike, onSkip]);

  const getPos = (e) => e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };

  const onPointerDown = (e) => {
    if (!isTop || exiting) return;
    startPos.current = getPos(e);
    isDragging.current = true;
    if (cardRef.current) cardRef.current.style.transition = 'none';
  };

  const onPointerMove = (e) => {
    if (!isDragging.current || !startPos.current || !cardRef.current) return;
    const dx = getPos(e).x - startPos.current.x;
    const dy = getPos(e).y - startPos.current.y;
    currentDx.current = dx;
    cardRef.current.style.transform = `translate(${dx}px, ${dy * 0.3}px) rotate(${dx * 0.06}deg)`;
    const pct = Math.min(Math.abs(dx) / 120, 1);
    setLikeOpacity(dx > 0 ? pct : 0);
    setNopeOpacity(dx < 0 ? pct : 0);
  };

  const onPointerUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const dx = currentDx.current;
    if (Math.abs(dx) > 100) {
      triggerExit(dx > 0 ? 'right' : 'left');
    } else {
      if (cardRef.current) {
        cardRef.current.style.transition = 'transform 0.4s ease';
        cardRef.current.style.transform = 'translate(0,0) rotate(0)';
      }
      setLikeOpacity(0);
      setNopeOpacity(0);
      currentDx.current = 0;
    }
  };

  const exitStyle = exiting === 'right'
    ? { transform: 'translate(150vw, -20px) rotate(25deg)', transition: 'transform 0.35s ease' }
    : exiting === 'left'
    ? { transform: 'translate(-150vw, -20px) rotate(-25deg)', transition: 'transform 0.35s ease' }
    : {};

  const lastSeenStr = profile.last_seen ? timeAgo(profile.last_seen) : null;

  return (
    <div
      ref={cardRef}
      className="profile-card"
      style={{ cursor: isTop ? 'grab' : 'default', zIndex: isTop ? 2 : 1, ...exitStyle }}
      onMouseDown={onPointerDown}
      onMouseMove={onPointerMove}
      onMouseUp={onPointerUp}
      onMouseLeave={onPointerUp}
      onTouchStart={onPointerDown}
      onTouchMove={onPointerMove}
      onTouchEnd={onPointerUp}
    >
      <div className="swipe-indicator like" style={{ opacity: likeOpacity }}>ME GUSTA</div>
      <div className="swipe-indicator nope" style={{ opacity: nopeOpacity }}>PASO</div>

      <div className="profile-card-photo">
        {profile.foto_path
          ? <img src={profile.foto_path} alt={profile.name} draggable={false} />
          : <div className="profile-card-avatar">{(profile.name || 'U')[0].toUpperCase()}</div>
        }
        {profile.nota && <div className="nota-burbuja nota-burbuja--card">{profile.nota}</div>}
        {(profile.fotos || []).length > 1 && <span className="profile-card-fotos">📷 {profile.fotos.length}</span>}
        <div className="profile-card-overlay">
          {profile.vip && <span className="vip-badge">👑 Usuario VIP</span>}
          {profile.afinidad != null && <span className="afinidad-badge">💞 {profile.afinidad}% afinidad</span>}
          <div className="profile-card-name">{profile.name}{profile.age ? `, ${profile.age}` : ''}<BadgeVerificado show={profile.verificado} dorado={profile.verif_dorado} /></div>
          <div className="profile-card-dept">📍 {lugarDe(profile)}</div>
          {lastSeenStr && <div className="profile-card-last-seen">{lastSeenStr}</div>}
        </div>
      </div>

      <div className="profile-card-body">
        {profile.bio && <p className="profile-card-bio">{profile.bio}</p>}
        {(profile.intereses || []).length > 0 && (
          <div className="profile-card-interests">
            {profile.intereses.slice(0, 6).map(i => (
              <span key={i} className="interest-chip">{i}</span>
            ))}
          </div>
        )}
        {/* Info extra */}
        <div className="profile-card-meta">
          {profile.educacion && <span className="meta-chip">🎓 {profile.educacion}</span>}
          {profile.estilo_vida && <span className="meta-chip">✨ {profile.estilo_vida}</span>}
          {profile.estado_civil && profile.estado_civil !== 'Prefiero no decir' && <span className="meta-chip">💍 {profile.estado_civil}</span>}
          {profile.tiene_hijos && profile.tiene_hijos !== 'Prefiero no decir' && <span className="meta-chip">👶 {hijosLabel(profile.tiene_hijos)}</span>}
          {profile.mascota && <span className="meta-chip">🐾 {profile.mascota === 'No tengo' ? 'Sin mascotas' : profile.mascota}</span>}
          {profile.genero && profile.genero !== 'Prefiero no decir' && <span className="meta-chip">⚧ {profile.genero}</span>}
          {profile.orientacion && profile.orientacion !== 'Prefiero no decir' && <span className="meta-chip">{profile.orientacion}</span>}
          {(profile.busca_relacion || []).length > 0 && <span className="meta-chip">💞 Busca: {profile.busca_relacion.join(', ')}</span>}
          {profile.altura && <span className="meta-chip">📏 {profile.altura} cm</span>}
          {profile.contextura && <span className="meta-chip">💪 {profile.contextura}</span>}
          {(profile.personalidad || []).map(t => <span key={t} className="meta-chip">✨ {t}</span>)}
        </div>
        {/* (guiño movido a fila de acciones) */}
      </div>

      {/* La tarjeta corta lo que no entra: este botón abre todas las fotos y todos los datos */}
      <div className="profile-card-ver-mas">
        <button className="btn btn-sm btn-outline" onClick={onVerPerfil}
          onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}>
          👤 Ver perfil completo
        </button>
      </div>
    </div>
  );
}

function StandoutsSection({ onLike, onWink, onVerPerfil, plan }) {
  const [standouts, setStandouts] = useState([]);
  const { error } = useToast();

  useEffect(() => {
    api.getStandouts().then(d => setStandouts(d.standouts || [])).catch(() => {});
  }, []);

  if (!standouts.length) return null;

  return (
    <div className="standouts-section">
      <h3 className="standouts-title">⭐ Destacados para vos</h3>
      <div className="standouts-grid">
        {standouts.map(p => (
          <div key={p.id} className={`standout-card${p.vip ? ' standout-card--vip' : ''}`}>
            <div className="standout-photo standout-ver" onClick={() => onVerPerfil(p)} role="button" tabIndex={0} title="Ver perfil completo">
              {p.foto_path
                ? <img src={p.foto_path} alt={p.name} />
                : <div className="standout-avatar">{(p.name||'U')[0]}</div>
              }
              {p.vip && <span className="vip-badge vip-badge--sm">👑 Usuario VIP</span>}
            </div>
            <div className="standout-info standout-ver" onClick={() => onVerPerfil(p)} role="button" tabIndex={0} title="Ver perfil completo">
              <div className="standout-name">{p.name}{p.age ? `, ${p.age}` : ''}<BadgeVerificado show={p.verificado} dorado={p.verif_dorado} /></div>
              <div className="standout-dept">📍 {lugarDe(p)}</div>
              {p.bio && <div className="standout-bio">{p.bio.slice(0, 60)}...</div>}
            </div>
            <div className="standout-actions">
              <button className="btn btn-sm btn-outline" onClick={() => onWink(p.id)}>💌</button>
              <button className="btn btn-sm btn-primary" onClick={() => onLike(p.id, 'like')}>💚</button>
              {plan !== 'free' && <button className="btn btn-sm btn-superlikes" onClick={() => onLike(p.id, 'super_like')} title="Flechazo">💘</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Fecha de SQLite (UTC, 'AAAA-MM-DD HH:MM:SS') a Date
const fechaSql = (v) => v ? new Date(v.replace(' ', 'T') + 'Z') : null;

// Premium: un impulso de 30 minutos gratis por semana
function ImpulsoSemanal({ user, onUsado }) {
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState('');
  const ahora = new Date();
  const activo = fechaSql(user.boost_until);
  const usado = fechaSql(user.impulso_semanal_at);
  const proximo = usado ? new Date(usado.getTime() + 7 * 24 * 3600 * 1000) : null;
  const disponible = !proximo || proximo <= ahora;

  const usar = async () => {
    setEnviando(true); setMsg('');
    try { await api.impulsoSemanal(); await onUsado(); }
    catch (e) { setMsg(e.message); }
    finally { setEnviando(false); }
  };

  return (
    <div className="impulso-semanal">
      {activo && activo > ahora && (
        <div className="impulso-activo">⚡ Tu perfil sale primero hasta las {activo.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</div>
      )}
      {disponible ? (
        <button className="btn btn-dorado btn-sm" style={{ width: '100%' }} onClick={usar} disabled={enviando}>
          {enviando ? 'Activando…' : '⚡ Usar mi impulso semanal'}
        </button>
      ) : (
        <div className="impulso-proximo">⚡ Impulso semanal usado. El próximo, el {proximo.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'numeric' })} a las {proximo.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</div>
      )}
      {msg && <div className="impulso-proximo">{msg}</div>}
    </div>
  );
}

export default function ExplorePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, error, success } = useToast();

  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filterProv, setFilterProv] = useState('Todas');
  const [filterDept, setFilterDept] = useState('Todos');
  const [filterInteres, setFilterInteres] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filterEducacion, setFilterEducacion] = useState('');
  const [filterEstilo, setFilterEstilo] = useState('');
  const [filterHijos, setFilterHijos] = useState('');
  const [filterQuiereHijos, setFilterQuiereHijos] = useState('');
  const [filterReligion, setFilterReligion] = useState('');
  const [filterGenero, setFilterGenero] = useState('');
  const [filterEstadoCivil, setFilterEstadoCivil] = useState('');
  const [filterOrientacion, setFilterOrientacion] = useState('');
  const [filterBusca, setFilterBusca] = useState('');
  const [filterContextura, setFilterContextura] = useState('');
  const [filterPersonalidad, setFilterPersonalidad] = useState('');
  const [filterAlturaMin, setFilterAlturaMin] = useState('');
  const [filterAlturaMax, setFilterAlturaMax] = useState('');
  const [filterVerificado, setFilterVerificado] = useState(false);
  const [matchModal, setMatchModal] = useState(null);
  // Perfil completo abierto: { profile } (tarjeta actual) o { id } (se pide al servidor)
  const [verPerfil, setVerPerfil] = useState(null);
  const [matches, setMatches] = useState([]);
  const [winks, setWinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [likesLeft, setLikesLeft] = useState(null);
  const [superLikesLeft, setSuperLikesLeft] = useState(null);
  const [activeTab, setActiveTab] = useState('swipe'); // 'swipe' | 'liked-me' | 'viewers' | 'winks'
  const [whoLikedMe, setWhoLikedMe] = useState([]);
  const [whoViewed, setWhoViewed] = useState([]);

  // Filtros elegidos en el panel (se aplican recién al tocar «Buscar»)
  const filtrosDelPanel = () => ({
    provincia: filterProv === 'Todas' ? '' : filterProv,
    departamento: filterProv === 'Todas' ? '' : filterDept,
    interes: filterInteres.trim(),
    educacion: filterEducacion,
    estilo_vida: filterEstilo,
    tiene_hijos: filterHijos,
    quiere_hijos: filterQuiereHijos,
    religion: filterReligion,
    genero: filterGenero,
    estado_civil: filterEstadoCivil,
    orientacion: filterOrientacion,
    busca: filterBusca,
    contextura: filterContextura,
    personalidad: filterPersonalidad,
    altura_min: filterAlturaMin,
    altura_max: filterAlturaMax,
    verificado: filterVerificado ? '1' : '',
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState({});
  const hayFiltrosAplicados = Object.values(filtrosAplicados).some(v => v && v !== 'Todos');
  const resultadosRef = useRef(null);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getProfiles(filtrosAplicados);
      setProfiles(data.profiles || []);
      setCurrentIndex(0);
    } catch { error('Error cargando perfiles.'); }
    finally { setLoading(false); }
  }, [filtrosAplicados, error]);

  // En el celular los resultados quedan debajo del panel: se baja hasta ellos
  const mostrarResultados = () => {
    if (window.matchMedia('(max-width: 900px)').matches && resultadosRef.current) {
      setTimeout(() => resultadosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  };

  const buscar = () => {
    setActiveTab('swipe');
    setFiltrosAplicados(filtrosDelPanel());
    mostrarResultados();
  };

  const verTodos = () => {
    setFilterProv('Todas'); setFilterDept('Todos'); setFilterInteres(''); setFilterEducacion(''); setFilterEstilo(''); setFilterHijos(''); setFilterQuiereHijos(''); setFilterReligion('');
    setFilterGenero(''); setFilterEstadoCivil(''); setFilterOrientacion(''); setFilterBusca(''); setFilterContextura(''); setFilterPersonalidad(''); setFilterAlturaMin(''); setFilterAlturaMax(''); setFilterVerificado(false);
    setShowAdvanced(false);
    setActiveTab('swipe');
    setFiltrosAplicados({});
    mostrarResultados();
  };

  const loadMatches = useCallback(async () => {
    try { const d = await api.getMatches(); setMatches(d.matches || []); } catch {}
  }, []);

  const loadWinks = useCallback(async () => {
    try { const d = await api.getWinks(); setWinks(d.winks || []); } catch {}
  }, []);

  const loadWhoLikedMe = useCallback(async () => {
    try { const d = await api.whoLikedMe(); setWhoLikedMe(d.likers || []); } catch {}
  }, []);

  const loadWhoViewed = useCallback(async () => {
    try { const d = await api.whoViewedMe(); setWhoViewed(d.viewers || []); } catch {}
  }, []);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);
  useEffect(() => { loadMatches(); loadWinks(); }, [loadMatches, loadWinks]);

  // Activate tab from URL param (Navbar links to /explorar?tab=liked-me etc.)
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (!tab) return;
    if (tab === 'liked-me') {
      if (user?.plan !== 'premium') { error('Ver quién te dio me gusta es exclusivo del Plan Premium.'); return; }
      setActiveTab('liked-me');
      loadWhoLikedMe();
    } else if (tab === 'viewers') {
      setActiveTab('viewers');
      loadWhoViewed();
    } else if (tab === 'standouts') {
      setActiveTab('standouts');
    }
  }, [location.search, user?.plan, error, loadWhoLikedMe, loadWhoViewed]);

  useEffect(() => {
    if (user) {
      setLikesLeft(user.plan === 'free' ? user.likes_restantes : null);
      setSuperLikesLeft(user.super_likes_left || 0);
    }
  }, [user]);

  const showFeedback = (type) => {
    setActionFeedback(type);
    setTimeout(() => setActionFeedback(null), 700);
  };

  const doLike = useCallback(async (profileId, tipo = 'like') => {
    if (!profileId) return;
    showFeedback(tipo === 'super_like' ? 'super' : 'like');
    setCurrentIndex(i => i + 1);
    try {
      const result = await api.likeUser(profileId, tipo);
      if (result.isMatch) {
        const profile = profiles.find(p => p.id === profileId);
        if (profile) setMatchModal(profile);
        refreshUser();
        loadMatches();
      }
      if (result.likesRestantes !== undefined && result.likesRestantes !== -1) {
        setLikesLeft(result.likesRestantes);
      }
    } catch (e) {
      if (e.message?.includes('LIKES_EXHAUSTED')) {
        error('Agotaste tus 20 me gusta del día. ¡Mejorá tu plan!');
        setCurrentIndex(i => i - 1);
      } else if (e.message?.includes('SUPERLIKES_EXHAUSTED')) {
        error('Agotaste tus flechazos de la semana.');
        setCurrentIndex(i => i - 1);
      } else {
        error(e.message || 'Error al dar me gusta.');
        setCurrentIndex(i => i - 1);
      }
    }
  }, [profiles, refreshUser, loadMatches]);

  const handleLike = useCallback(() => {
    const profile = profiles[currentIndex];
    if (profile) {
      api.skipUser(profile.id).catch(() => {});
      doLike(profile.id, 'like');
    }
  }, [profiles, currentIndex, doLike]);

  const handleSuperLike = useCallback(() => {
    const profile = profiles[currentIndex];
    if (!profile) return;
    if (user?.plan === 'free') { error('El flechazo está disponible desde el Plan Mate 💘'); return; }
    doLike(profile.id, 'super_like');
  }, [profiles, currentIndex, user, doLike]);

  const handleSkip = useCallback(async () => {
    const profile = profiles[currentIndex];
    if (profile) {
      try { await api.skipUser(profile.id); } catch {}
    }
    showFeedback('nope');
    setCurrentIndex(i => i + 1);
  }, [profiles, currentIndex]);

  const handleRewind = useCallback(async () => {
    if (user?.plan === 'free') { error('Deshacer es una función de los planes pagos. ¡Mejorá tu plan!'); return; }
    try {
      const result = await api.rewind();
      if (result.ok) {
        success('↩ Acción deshecha.');
        loadProfiles();
      }
    } catch (e) { error(e.message || 'No hay acción para deshacer.'); }
  }, [user, loadProfiles]);

  const handleWink = useCallback(async (profileId) => {
    try {
      await api.winkUser(profileId);
      success('💌 Guiño enviado!');
    } catch (e) { error(e.message || 'Error al enviar guiño.'); }
  }, []);

  const handleLikeFromStandout = useCallback(async (profileId, tipo) => {
    try {
      const result = await api.likeUser(profileId, tipo);
      if (result.isMatch) {
        const p = { id: profileId };
        setMatchModal(p);
        loadMatches();
      }
      success(tipo === 'super_like' ? '💘 ¡Flechazo enviado!' : '💚 ¡Me gusta enviado!');
    } catch (e) { error(e.message); }
  }, [loadMatches]);

  const currentProfile = profiles[currentIndex];
  const nextProfile = profiles[currentIndex + 1];

  return (
    <div className="explore-page">
      <Navbar />
      <ToastContainer toasts={toasts} />
      <WelcomeBot />

      {verPerfil && (
        <PerfilCompleto
          profile={verPerfil.profile}
          cargarId={verPerfil.id}
          onClose={() => setVerPerfil(null)}
          acciones={verPerfil.profile && verPerfil.profile.id === currentProfile?.id && (
            <>
              <button className="btn btn-outline" onClick={() => { setVerPerfil(null); handleSkip(); }}>✕ Paso</button>
              <button className="btn btn-outline" onClick={() => handleWink(verPerfil.profile.id)}>💌 Guiño</button>
              <button className="btn btn-primary" onClick={() => { setVerPerfil(null); handleLike(); }}>💚 Me gusta</button>
            </>
          )}
        />
      )}

      {matchModal && (
        <MatchModal
          matchedUser={matchModal}
          onClose={() => setMatchModal(null)}
          onChat={() => { setMatchModal(null); navigate('/chat'); }}
        />
      )}

      <div className="explore-container container">
        <div className="explore-header">
          <h1 className="explore-title">Explorar</h1>
          <p className="explore-sub">
            {!loading && profiles.length - currentIndex > 0
              ? `${profiles.length - currentIndex} perfiles disponibles`
              : !loading ? 'Sin más perfiles por ahora' : ''}
          </p>
        </div>

        <DeliveryAviso user={user} onVerPlanes={() => navigate('/planes')} />

        <div className="explore-layout">
          {/* SIDEBAR */}
          <aside className="explore-filters">
            <NotaEditor user={user} onGuardada={refreshUser} />
            {!user?.compat_respondidas && (
              <button className="afinidad-cta" onClick={() => navigate('/afinidad')}>
                💞 Hacé el test de afinidad sanjuanino y mirá qué tan compatibles son
              </button>
            )}
            {user && !user.verificado && (
              <button className="afinidad-cta verif-cta" onClick={() => navigate('/verificacion')}>
                ✔️ Verificá tu perfil con una selfie y sumá el tilde azul: te van a dar más me gusta
              </button>
            )}
            {user?.plan === 'premium' && <ImpulsoSemanal user={user} onUsado={refreshUser} />}
            <h3 className="filter-title">🔍 Filtros</h3>
            <label className="filtro-verificados">
              <input type="checkbox" checked={filterVerificado} onChange={e => setFilterVerificado(e.target.checked)} />
              Solo perfiles verificados ✔️
            </label>
            <div className="form-group">
              <label className="form-label">Provincia</label>
              <select className="form-input" value={filterProv} onChange={e => { setFilterProv(e.target.value); setFilterDept('Todos'); }}>
                <option value="Todas">Todas</option>
                {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Departamento</label>
              <select className="form-input" value={filterDept} disabled={filterProv === 'Todas'} onChange={e => setFilterDept(e.target.value)}>
                <option value="Todos">{filterProv === 'Todas' ? 'Elegí primero la provincia' : 'Todos'}</option>
                {(DEPARTAMENTOS_POR_PROVINCIA[filterProv] || []).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Busca</label>
              <select className="form-input" value={filterBusca} onChange={e => setFilterBusca(e.target.value)}>
                {BUSCA_OPS.map(o => <option key={o} value={o}>{o || 'Cualquier cosa'}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Género</label>
              <select className="form-input" value={filterGenero} onChange={e => setFilterGenero(e.target.value)}>
                {GENERO_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Interés</label>
              <input className="form-input" type="text" placeholder="ej: mate, fútbol..."
                value={filterInteres} onChange={e => setFilterInteres(e.target.value)} />
            </div>

            {/* Filtros avanzados */}
            <button
              className="btn btn-outline btn-sm"
              style={{ marginBottom: 8, width: '100%' }}
              onClick={() => setShowAdvanced(v => !v)}
            >
              {showAdvanced ? '▲ Menos filtros' : '▼ Filtros avanzados'}
            </button>

            {showAdvanced && (
              <div className="advanced-filters">
                <div className="form-group">
                  <label className="form-label">Orientación</label>
                  <select className="form-input" value={filterOrientacion} onChange={e => setFilterOrientacion(e.target.value)}>
                    {ORIENTACION_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Estado civil</label>
                  <select className="form-input" value={filterEstadoCivil} onChange={e => setFilterEstadoCivil(e.target.value)}>
                    {ESTADO_CIVIL_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Altura (cm)</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="form-input" type="number" inputMode="numeric" min="120" max="230" placeholder="Desde"
                      value={filterAlturaMin} onChange={e => setFilterAlturaMin(e.target.value.replace(/\D/g, ''))} />
                    <input className="form-input" type="number" inputMode="numeric" min="120" max="230" placeholder="Hasta"
                      value={filterAlturaMax} onChange={e => setFilterAlturaMax(e.target.value.replace(/\D/g, ''))} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Contextura</label>
                  <select className="form-input" value={filterContextura} onChange={e => setFilterContextura(e.target.value)}>
                    {CONTEXTURA_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Personalidad</label>
                  <select className="form-input" value={filterPersonalidad} onChange={e => setFilterPersonalidad(e.target.value)}>
                    {PERSONALIDAD_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Educación</label>
                  <select className="form-input" value={filterEducacion} onChange={e => setFilterEducacion(e.target.value)}>
                    {EDUCACION_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Estilo de vida</label>
                  <select className="form-input" value={filterEstilo} onChange={e => setFilterEstilo(e.target.value)}>
                    {ESTILO_VIDA_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Hijos</label>
                  <select className="form-input" value={filterHijos} onChange={e => setFilterHijos(e.target.value)}>
                    {HIJOS_OPS.map(o => <option key={o} value={o}>{o ? hijosLabel(o) : 'Cualquiera'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Quiere hijos</label>
                  <select className="form-input" value={filterQuiereHijos} onChange={e => setFilterQuiereHijos(e.target.value)}>
                    {QUIERE_HIJOS_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Religión</label>
                  <select className="form-input" value={filterReligion} onChange={e => setFilterReligion(e.target.value)}>
                    {RELIGION_OPS.map(o => <option key={o} value={o}>{o || 'Cualquiera'}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="filtros-acciones">
              <button className="btn btn-primary filtro-buscar" onClick={buscar}>
                🔍 Buscar
              </button>
              <button className="btn btn-outline filter-reset btn-sm" onClick={verTodos}>
                👥 Ver todos los perfiles
              </button>
            </div>

            <div className="matches-sidebar">
              <h3 className="filter-title">💕 Mis conexiones ({matches.length})</h3>
              {matches.length === 0
                ? <p className="no-matches-hint">¡Seguí explorando para hacer tu primera conexión!</p>
                : <button className="btn btn-primary btn-sm" onClick={() => navigate('/chat')}>
                    Ver chats 💬
                  </button>
              }
            </div>

            {/* Winks */}
            {winks.length > 0 && (
              <div className="winks-sidebar">
                <h3 className="filter-title">💌 Guiños ({winks.filter(w => !w.seen).length > 0 ? <span className="badge-new">NUEVO</span> : ''}{winks.length})</h3>
                {winks.slice(0, 3).map(w => (
                  <div key={w.id} className="wink-item">
                    {w.foto_path
                      ? <img src={w.foto_path} alt={w.name} className="wink-avatar" />
                      : <div className="wink-avatar wink-avatar-letter">{(w.name||'U')[0]}</div>
                    }
                    <span>{w.name}{w.age ? `, ${w.age}` : ''}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Contadores plan */}
            {user?.plan === 'free' && likesLeft !== null && (
              <div className="plan-info-box">
                <div>💚 Me gusta hoy: <strong>{likesLeft}/20</strong></div>
                <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>Se renuevan cada 24hs</div>
                <button className="btn btn-dorado btn-sm" style={{ marginTop: 8, width: '100%' }} onClick={() => navigate('/suscripcion')}>
                  ☕ Me gusta ilimitados
                </button>
              </div>
            )}

            {(user?.plan === 'mate' || user?.plan === 'premium') && superLikesLeft !== null && (
              <div className="plan-info-box" style={{ background: 'rgba(255,215,0,0.07)', borderColor: 'rgba(255,215,0,0.2)' }}>
                <div>💘 Flechazos: <strong>{superLikesLeft}/{user?.plan === 'premium' ? 10 : 3}</strong> esta semana</div>
              </div>
            )}
          </aside>

          {/* MAIN */}
          <main className="explore-main" ref={resultadosRef}>
              {/* SWIPE TAB */}
            {activeTab === 'swipe' && (
              <>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: 60, color: 'var(--texto-suave)' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🧉</div>
                    <p>Buscando perfiles...</p>
                  </div>
                ) : !currentProfile ? (
                  <>
                    <div className="no-more-profiles">
                      <div className="no-more-icon">🧉</div>
                      <h2>Ya viste todos los perfiles</h2>
                      <p>Volvé más tarde o cambiá los filtros.</p>
                      <button className="btn btn-primary" onClick={loadProfiles}>Actualizar</button>
                      {hayFiltrosAplicados && (
                        <button className="btn btn-outline" style={{ marginTop: 8 }} onClick={verTodos}>👥 Ver todos los perfiles</button>
                      )}
                      <button className="btn btn-outline" style={{ marginTop: 8 }} onClick={() => setActiveTab('standouts')}>⭐ Ver destacados</button>
                    </div>
                  </>
                ) : (
                  <div className="swipe-wrapper">
                    {actionFeedback === 'like' && <div className="action-feedback like-feedback">💚 ME GUSTA</div>}
                    {actionFeedback === 'nope' && <div className="action-feedback nope-feedback">✕ PASO</div>}
                    {actionFeedback === 'super' && <div className="action-feedback super-feedback">💘 ¡FLECHAZO!</div>}

                    <div className="swipe-area">
                      {nextProfile && (
                        <div className="profile-card profile-card-behind">
                          <div className="profile-card-photo">
                            {nextProfile.foto_path
                              ? <img src={nextProfile.foto_path} alt={nextProfile.name} />
                              : <div className="profile-card-avatar">{(nextProfile.name || 'U')[0].toUpperCase()}</div>
                            }
                            <div className="profile-card-overlay">
                              <div className="profile-card-name">{nextProfile.name}<BadgeVerificado show={nextProfile.verificado} dorado={nextProfile.verif_dorado} /></div>
                            </div>
                          </div>
                        </div>
                      )}
                      <ProfileCard
                        key={currentProfile.id}
                        profile={currentProfile}
                        onLike={handleLike}
                        onSuperLike={handleSuperLike}
                        onSkip={handleSkip}
                        onWink={handleWink}
                        onVerPerfil={() => setVerPerfil({ profile: currentProfile })}
                        isTop={true}
                        plan={user?.plan}
                      />
                    </div>

                    {/* BOTONES DE ACCIÓN */}
                    <div className="tinder-actions">
                      <button className="tinder-btn tinder-skip" onClick={handleSkip} title="Pasar" aria-label="Pasar">
                        <span className="tinder-btn-icon">✕</span>
                        <span className="tinder-btn-label">PASO</span>
                      </button>

                      {user?.plan !== 'free' && (
                        <button className="tinder-btn tinder-rewind" onClick={handleRewind} title="Deshacer" aria-label="Deshacer">
                          <span className="tinder-btn-icon">↩</span>
                          <span className="tinder-btn-label">DESHACER</span>
                        </button>
                      )}

                      <button
                        className="tinder-btn tinder-wink-action"
                        onClick={() => currentProfile && handleWink(currentProfile.id)}
                        title="Enviar guiño"
                        aria-label="Guiño"
                      >
                        <span className="tinder-btn-icon">💌</span>
                        <span className="tinder-btn-label">GUIÑO</span>
                      </button>

                      {user?.plan !== 'free' && (
                        <button className="tinder-btn tinder-superlikes" onClick={handleSuperLike} title="Flechazo" aria-label="Flechazo">
                          <span className="tinder-btn-icon">💘</span>
                          <span className="tinder-btn-label">FLECHAZO</span>
                        </button>
                      )}

                      <button className="tinder-btn tinder-like" onClick={handleLike} title="Me gusta" aria-label="Me gusta">
                        <span className="tinder-btn-icon">💚</span>
                        <span className="tinder-btn-label">ME GUSTA</span>
                      </button>
                    </div>

                    <p className="swipe-hint-text">Deslizá la tarjeta o usá los botones</p>
                  </div>
                )}

              </>
            )}

            {/* QUIÉN TE DIO LIKE TAB */}
            {activeTab === 'liked-me' && (
              <div className="who-liked-grid">
                <h2 style={{ marginBottom: 16 }}>💛 Perfiles que te dieron me gusta</h2>
                {whoLikedMe.length === 0
                  ? <p style={{ color: 'var(--texto-suave)' }}>Todavía nadie te dio me gusta. ¡Seguí explorando!</p>
                  : whoLikedMe.map(l => (
                    <div key={l.id} className="who-liked-card">
                      <button className="who-liked-ver" onClick={() => setVerPerfil({ id: l.id, profile: l })} aria-label={`Ver el perfil de ${l.name}`}>
                      {l.foto_path
                        ? <img src={l.foto_path} alt={l.name} className="who-liked-photo" />
                        : <div className="who-liked-avatar">{(l.name||'U')[0]}</div>
                      }
                      <div className="who-liked-info">
                        <div className="who-liked-name">{l.name}{l.age ? `, ${l.age}` : ''}</div>
                        <div className="who-liked-dept">📍 {lugarDe(l)}</div>
                        {l.tipo === 'super_like' && <div className="super-like-badge">💘 Flechazo</div>}
                        <div className="who-liked-ver-texto">Ver perfil completo ›</div>
                      </div>
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={() => doLike(l.id, 'like')}>
                        💚 Devolver el me gusta
                      </button>
                    </div>
                  ))
                }
              </div>
            )}

            {/* DESTACADOS TAB */}
            {activeTab === 'standouts' && (
              <StandoutsSection onLike={handleLikeFromStandout} onWink={handleWink} onVerPerfil={p => setVerPerfil({ id: p.id, profile: p })} plan={user?.plan} />
            )}

            {/* QUIÉN TE VIO TAB (desde Navbar) */}
            {activeTab === 'viewers' && (
              <div className="who-liked-grid">
                <h2 style={{ marginBottom: 8 }}>👁️ Quién vio tu perfil esta semana</h2>
                <p style={{ color: 'var(--texto-suave)', fontSize: 13, marginBottom: 16 }}>
                  {user?.plan === 'free' && 'Plan Gratis: últimos 3 visitantes. '}
                  {user?.plan === 'mate' && 'Plan Mate: últimos 10 visitantes. '}
                  {user?.plan === 'premium' && 'Plan Premium: todos los visitantes. '}
                </p>
                {whoViewed.length === 0
                  ? <p style={{ color: 'var(--texto-suave)' }}>Aún nadie vio tu perfil esta semana.</p>
                  : whoViewed.map(v => (
                    <div key={v.id} className="who-liked-card">
                      <button className="who-liked-ver" onClick={() => setVerPerfil({ id: v.id, profile: v })} aria-label={`Ver el perfil de ${v.name}`}>
                      {v.foto_path
                        ? <img src={v.foto_path} alt={v.name} className="who-liked-photo" />
                        : <div className="who-liked-avatar">{(v.name||'U')[0]}</div>
                      }
                      <div className="who-liked-info">
                        <div className="who-liked-name">{v.name}{v.age ? `, ${v.age}` : ''}</div>
                        <div className="who-liked-dept">📍 {lugarDe(v)}</div>
                        <div style={{ fontSize: 11, opacity: 0.6 }}>{new Date(v.viewed_at).toLocaleDateString('es-AR')}</div>
                        <div className="who-liked-ver-texto">Ver perfil completo ›</div>
                      </div>
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => doLike(v.id, 'like')}>
                        💚 Like
                      </button>
                    </div>
                  ))
                }
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
